import { useState, useCallback, useRef, useEffect } from "react";
import { sendOtp, verifyOtp, getNextUid } from "../utils/api";

const SUBMIT_KEY = "contactPopupSubmittedAt";
const TTL_MS = 24 * 60 * 60 * 1000;

export function useLeadForm({ projectId, formDefaults = {} }) {
  const [modal, setModal] = useState("closed");
  const [c2aEvent, setC2aEvent] = useState("PopUp");

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);

  const uidRef = useRef(null);
  const autoPopupRef = useRef(null);
  const countdownRef = useRef(null);

  // ============================
  // ✅ SAFE ACCESSORS
  // ============================
  const getProjectName = useCallback(
    () => formDefaults?.project || "",
    [formDefaults]
  );

  const getLocation = useCallback(
    () => formDefaults?.location || "",
    [formDefaults]
  );

  // ============================
  // ✅ CHECK 24h SUBMISSION
  // ============================
  const isAlreadySubmitted = useCallback(() => {
    try {
      const ts = localStorage.getItem(SUBMIT_KEY);
      if (!ts) return false;
      return Date.now() - Number(ts) < TTL_MS;
    } catch {
      return false;
    }
  }, []);

  // ============================
  // ✅ OPEN POPUP
  // FIX: accept event label from every button click and store it
  // ============================
  const openPopup = useCallback(
    (event = "PopUp") => {
      // event is the button label e.g. "Download Brochure", "Book Site Visit", "PopUp"
      setC2aEvent(event);

      if (isAlreadySubmitted()) {
        setModal("already");
      } else {
        setModal("contact");
      }
    },
    [isAlreadySubmitted]
  );

  const closeAll = useCallback(() => {
    setModal("closed");
  }, []);

  // ============================
  // 🔥 AUTO POPUP (ONCE)
  // ============================
 useEffect(() => {
  // ⛔ wait until project + location loaded
  if (!formDefaults?.project || !formDefaults?.location) return;

  if (isAlreadySubmitted()) return;

  autoPopupRef.current = setTimeout(() => {
    setModal((prev) => (prev === "closed" ? "contact" : prev));
  }, 30000);

  return () => clearTimeout(autoPopupRef.current);
}, [formDefaults, isAlreadySubmitted]);

  // ============================
  // ⏳ RESEND COUNTDOWN
  // ============================
  const startResendCountdown = () => {
    clearInterval(countdownRef.current);
    setResendCountdown(60);
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================
  // 📩 SEND OTP
  // FIX: always use formDefaults for projectName & location (single source of truth)
  //      pass c2aEvent so pre-lead API receives the button label
  // ============================
  const handleSendOtp = useCallback(
  async ({ fullName, phone, email }) => {
    setLoading(true);
    setOtpError("");

    try {
      // ✅ generate UID once per session
      if (!uidRef.current) {
        uidRef.current = await getNextUid(); // already string
      }

      const uid = uidRef.current;

      // ✅ store everything INCLUDING uniqueId
      setFormData({
        fullName,
        phone,
        email: email || "",
        uniqueId: uid,   // 🔥 CRITICAL FIX
      });

      const payload = {
        phone,
        fullName,
        projectName: getProjectName(),
        location: getLocation(),
        domainName: window.location.hostname.replace("www.", ""),
        uniqueId: uid,  // ✅ correct
        c2aEvent: c2aEvent || "Form Submit",
      };

      console.log("📤 SEND OTP:", payload);

      const result = await sendOtp(payload);

      if (result?.error) throw new Error(result.error);

      setModal("otp");
      startResendCountdown();
    } catch (e) {
      setOtpError(e.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  },
  [getProjectName, getLocation, c2aEvent]
);

  // ============================
  // 🔐 VERIFY OTP
  // FIX: projectName, location, c2aEvent all reliably resolved
  // ============================
  const handleVerifyOtp = useCallback(
  async (otp) => {
    setLoading(true);
    setOtpError("");

    try {
      const { fullName, phone, email, uniqueId } = formData;

      const payload = {
        phone,
        otp,
        fullName,
        email,
        projectName: getProjectName(),
        location: getLocation(),
        domainName: window.location.hostname.replace("www.", ""),
        uniqueId: uniqueId,   // ✅ SAME ID
        c2aEvent: c2aEvent || "Form Submit",
      };

      console.log("📥 VERIFY OTP:", payload);

      const result = await verifyOtp(payload);

      if (result?.error) {
        setOtpError(result.error);
        return;
      }

     localStorage.setItem(SUBMIT_KEY, Date.now().toString());
uidRef.current = null;

setModal("thankyou");

// ✅ GTM — fires the Element Visibility trigger on #thankYouPopup
window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  event: "thankYouPopupVisible",
  lead_name: fullName,
  lead_phone: phone,
  project_name: getProjectName(),
  lead_source: c2aEvent || "Form Submit",
});
      setTimeout(() => {
        setModal("closed");
      }, 4000);
    } catch (e) {
      setOtpError(e.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  },
  [formData, getProjectName, getLocation, c2aEvent]
);

  // ============================
  // 🔁 RESEND OTP
  // ============================
  const handleResendOtp = useCallback(async () => {
    if (resendCountdown > 0) return;
    await handleSendOtp(formData);
  }, [handleSendOtp, formData, resendCountdown]);

  // ============================
  // 🧹 CLEANUP
  // ============================
  useEffect(() => {
    return () => {
      clearTimeout(autoPopupRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  return {
    modal,
    loading,
    otpError,
    resendCountdown,
    formData,
    c2aEvent,       // exposed for debugging if needed

    // ✅ FIX: expose formDefaults so LeadPopup can pass project/location
    //    to ContactForm for display purposes (not for API — API uses getProjectName/getLocation)
    formDefaults,

    openPopup,
    closeAll,
    handleSendOtp,
    handleVerifyOtp,
    handleResendOtp,
    setFormData,
  };
}